import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut,
    Mail,
    Phone,
    Trash2,
    CheckCircle2,
    Inbox,
    Filter,
    ArrowLeft,
    RefreshCw,
    Calendar,
    Car as CarIcon,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ac_admin_token";

const inputCls =
    "w-full bg-[#1A1A1A] border border-white/10 text-white placeholder-white/30 px-4 py-3 text-sm focus:outline-none focus:border-[#9D4CDD] focus:ring-2 focus:ring-[#9D4CDD]/30 transition-all";

function authHeaders(token) {
    return { headers: { Authorization: `Bearer ${token}` } };
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function statusPill(status) {
    const map = {
        new: "bg-[#9D4CDD]/15 text-[#B366F9] border-[#9D4CDD]/40",
        contacted: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
        archived: "bg-white/5 text-white/40 border-white/10",
    };
    return (
        <span
            className={`inline-flex items-center px-2.5 py-1 text-[10px] font-heading font-bold tracking-[0.2em] uppercase border ${map[status] || map.new}`}
        >
            {status}
        </span>
    );
}

function LoginScreen({ onLoggedIn }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError("");
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            localStorage.setItem(TOKEN_KEY, data.access_token);
            toast.success("Welcome back.");
            onLoggedIn(data.access_token);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "Invalid credentials.";
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="admin-login-screen"
            className="min-h-screen bg-ac-bg flex items-center justify-center px-6"
        >
            <div className="w-full max-w-md">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 font-heading text-[11px] uppercase tracking-[0.28em] text-white/50 hover:text-[#9D4CDD] transition-colors mb-8"
                >
                    <ArrowLeft size={14} /> Back to site
                </Link>
                <div className="bg-[#121212] border border-white/5 p-8 md:p-10">
                    <div className="font-heading text-xs uppercase tracking-[0.32em] text-[#9D4CDD] font-bold mb-3">
                        / Admin Portal
                    </div>
                    <h1 className="font-heading text-3xl font-black uppercase tracking-tighter text-white">
                        Sign in
                    </h1>
                    <p className="mt-3 text-sm text-white/55">
                        Authorized AC Detailing staff only.
                    </p>

                    <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
                        <div>
                            <div className="font-heading text-[10px] uppercase tracking-[0.24em] text-white/60 font-bold mb-2">
                                Email
                            </div>
                            <input
                                data-testid="admin-login-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={inputCls}
                                placeholder="you@acdetailingaz.com"
                                required
                            />
                        </div>
                        <div>
                            <div className="font-heading text-[10px] uppercase tracking-[0.24em] text-white/60 font-bold mb-2">
                                Password
                            </div>
                            <input
                                data-testid="admin-login-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={inputCls}
                                required
                            />
                        </div>
                        {error && (
                            <div
                                data-testid="admin-login-error"
                                className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2"
                            >
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            data-testid="admin-login-submit"
                            disabled={submitting}
                            className="w-full bg-[#9D4CDD] text-white py-3.5 font-heading text-[11px] font-bold tracking-[0.28em] uppercase hover:bg-[#B366F9] transition-all hover:shadow-[0_0_24px_rgba(157,76,221,0.45)] disabled:opacity-60"
                        >
                            {submitting ? "Signing in..." : "Sign In"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

function MessageRow({ msg, onMark, onDelete, busy }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <div
            data-testid={`admin-msg-${msg.id}`}
            className="bg-[#121212] border border-white/5 p-5 hover:border-[#9D4CDD]/30 transition-colors"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-heading text-lg font-bold text-white">
                            {msg.name}
                        </h3>
                        {statusPill(msg.status)}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-white/60">
                        <a
                            href={`mailto:${msg.email}`}
                            className="inline-flex items-center gap-1.5 hover:text-[#9D4CDD]"
                        >
                            <Mail size={12} /> {msg.email}
                        </a>
                        <a
                            href={`tel:${msg.phone}`}
                            className="inline-flex items-center gap-1.5 hover:text-[#9D4CDD]"
                        >
                            <Phone size={12} /> {msg.phone}
                        </a>
                        {msg.vehicle_type && (
                            <span className="inline-flex items-center gap-1.5">
                                <CarIcon size={12} /> {msg.vehicle_type}
                            </span>
                        )}
                        {msg.preferred_date && (
                            <span className="inline-flex items-center gap-1.5">
                                <Calendar size={12} /> {msg.preferred_date}
                            </span>
                        )}
                    </div>
                    {msg.package && (
                        <div className="mt-2 text-xs">
                            <span className="font-heading text-[10px] uppercase tracking-[0.2em] text-white/40 mr-2">
                                Package
                            </span>
                            <span className="text-white/80">{msg.package}</span>
                        </div>
                    )}
                    {msg.addons && msg.addons.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {msg.addons.map((a) => (
                                <span
                                    key={a}
                                    className="text-[10px] font-heading uppercase tracking-[0.14em] bg-[#9D4CDD]/10 text-[#B366F9] border border-[#9D4CDD]/30 px-2 py-0.5"
                                >
                                    {a}
                                </span>
                            ))}
                        </div>
                    )}
                    <div
                        className={`mt-3 text-sm text-white/80 leading-relaxed whitespace-pre-wrap ${expanded ? "" : "line-clamp-3"}`}
                    >
                        {msg.message}
                    </div>
                    {msg.message && msg.message.length > 160 && (
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            className="mt-1 text-[10px] font-heading uppercase tracking-[0.2em] text-[#9D4CDD] hover:text-[#B366F9]"
                        >
                            {expanded ? "Show less" : "Read more"}
                        </button>
                    )}
                </div>
                <div className="flex flex-col items-end gap-3 min-w-[160px]">
                    <div className="text-[10px] font-heading uppercase tracking-[0.2em] text-white/40">
                        {formatDate(msg.created_at)}
                    </div>
                    <div className="flex items-center gap-2">
                        {msg.status !== "contacted" && (
                            <button
                                type="button"
                                data-testid={`admin-mark-contacted-${msg.id}`}
                                disabled={busy}
                                onClick={() => onMark(msg.id, "contacted")}
                                className="inline-flex items-center gap-1.5 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-[0.2em] hover:bg-emerald-500/25 transition-all disabled:opacity-50"
                            >
                                <CheckCircle2 size={12} /> Contacted
                            </button>
                        )}
                        {msg.status !== "new" && (
                            <button
                                type="button"
                                data-testid={`admin-mark-new-${msg.id}`}
                                disabled={busy}
                                onClick={() => onMark(msg.id, "new")}
                                className="inline-flex items-center gap-1.5 bg-[#9D4CDD]/15 text-[#B366F9] border border-[#9D4CDD]/30 px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-[0.2em] hover:bg-[#9D4CDD]/25 transition-all disabled:opacity-50"
                            >
                                Mark New
                            </button>
                        )}
                        <button
                            type="button"
                            data-testid={`admin-delete-${msg.id}`}
                            disabled={busy}
                            onClick={() => onDelete(msg.id)}
                            className="inline-flex items-center gap-1.5 bg-transparent text-white/60 border border-white/10 px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-[0.2em] hover:text-red-300 hover:border-red-400/40 transition-all disabled:opacity-50"
                            aria-label="Delete message"
                        >
                            <Trash2 size={12} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Inbox_({ token, onLogout }) {
    const [messages, setMessages] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, archived: 0 });
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const params = filter === "all" ? {} : { status_filter: filter };
            const [msgRes, statsRes] = await Promise.all([
                axios.get(`${API}/admin/messages`, { ...authHeaders(token), params }),
                axios.get(`${API}/admin/messages/stats`, authHeaders(token)),
            ]);
            setMessages(msgRes.data || []);
            setStats(statsRes.data || stats);
        } catch (err) {
            if (err?.response?.status === 401) {
                toast.error("Session expired. Please sign in again.");
                onLogout();
            } else {
                toast.error("Failed to load messages.");
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, token]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const handleMark = async (id, status) => {
        setBusyId(id);
        try {
            await axios.patch(
                `${API}/admin/messages/${id}`,
                { status },
                authHeaders(token)
            );
            toast.success(`Marked as ${status}.`);
            fetchAll();
        } catch {
            toast.error("Update failed.");
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this message? This cannot be undone.")) return;
        setBusyId(id);
        try {
            await axios.delete(`${API}/admin/messages/${id}`, authHeaders(token));
            toast.success("Message deleted.");
            fetchAll();
        } catch {
            toast.error("Delete failed.");
        } finally {
            setBusyId(null);
        }
    };

    const filters = [
        { id: "all", label: "All", count: stats.total },
        { id: "new", label: "New", count: stats.new },
        { id: "contacted", label: "Contacted", count: stats.contacted },
        { id: "archived", label: "Archived", count: stats.archived },
    ];

    return (
        <div data-testid="admin-inbox" className="min-h-screen bg-ac-bg text-white">
            <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <span className="inline-flex items-center justify-center w-9 h-9 border border-[#9D4CDD]/50 bg-[#9D4CDD]/10 font-heading font-black text-[#9D4CDD] text-sm">
                            AC
                        </span>
                        <div>
                            <div className="font-heading text-[10px] uppercase tracking-[0.28em] text-[#9D4CDD] font-bold">
                                Admin
                            </div>
                            <div className="font-heading text-base font-bold uppercase tracking-tight text-white">
                                Inbox
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            data-testid="admin-refresh"
                            onClick={fetchAll}
                            className="inline-flex items-center gap-2 bg-transparent text-white/70 border border-white/10 px-4 py-2 text-[11px] font-heading font-bold uppercase tracking-[0.24em] hover:text-white hover:border-white/30"
                        >
                            <RefreshCw size={12} /> Refresh
                        </button>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 bg-transparent text-white/70 border border-white/10 px-4 py-2 text-[11px] font-heading font-bold uppercase tracking-[0.24em] hover:text-white hover:border-white/30"
                        >
                            View site
                        </Link>
                        <button
                            type="button"
                            data-testid="admin-logout"
                            onClick={onLogout}
                            className="inline-flex items-center gap-2 bg-[#9D4CDD] text-white px-4 py-2 text-[11px] font-heading font-bold uppercase tracking-[0.24em] hover:bg-[#B366F9]"
                        >
                            <LogOut size={12} /> Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-10 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {filters.map((f) => {
                        const active = filter === f.id;
                        return (
                            <button
                                key={f.id}
                                type="button"
                                data-testid={`admin-filter-${f.id}`}
                                onClick={() => setFilter(f.id)}
                                className={`text-left p-5 border transition-all ${
                                    active
                                        ? "border-[#9D4CDD] bg-[#9D4CDD]/10"
                                        : "border-white/5 bg-[#121212] hover:border-[#9D4CDD]/30"
                                }`}
                            >
                                <div className="font-heading text-[10px] uppercase tracking-[0.24em] text-white/50 font-bold">
                                    {f.label}
                                </div>
                                <div className="mt-1 font-heading text-3xl font-black tracking-tighter text-white">
                                    {f.count}
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center gap-2 text-xs text-white/50">
                    <Filter size={12} />
                    <span className="font-heading uppercase tracking-[0.2em]">
                        Showing {filter === "all" ? "all" : filter} messages
                    </span>
                </div>

                {loading ? (
                    <div
                        data-testid="admin-loading"
                        className="text-center py-16 text-white/40 font-heading text-xs uppercase tracking-[0.24em]"
                    >
                        Loading messages…
                    </div>
                ) : messages.length === 0 ? (
                    <div
                        data-testid="admin-empty"
                        className="text-center py-20 bg-[#121212] border border-white/5"
                    >
                        <Inbox className="mx-auto text-white/30" size={28} />
                        <div className="mt-4 font-heading text-lg font-bold text-white">
                            No messages yet
                        </div>
                        <div className="mt-1 text-sm text-white/50">
                            New customer inquiries will appear here.
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {messages.map((msg) => (
                            <MessageRow
                                key={msg.id}
                                msg={msg}
                                busy={busyId === msg.id}
                                onMark={handleMark}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [verifying, setVerifying] = useState(!!token);

    useEffect(() => {
        if (!token) {
            setVerifying(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                await axios.get(`${API}/auth/me`, authHeaders(token));
                if (!cancelled) setVerifying(false);
            } catch {
                if (!cancelled) {
                    localStorage.removeItem(TOKEN_KEY);
                    setToken(null);
                    setVerifying(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const handleLogout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.success("Signed out.");
    };

    if (verifying) {
        return (
            <div className="min-h-screen bg-ac-bg flex items-center justify-center">
                <div className="text-white/40 font-heading text-xs uppercase tracking-[0.24em]">
                    Loading…
                </div>
            </div>
        );
    }

    if (!token) {
        return <LoginScreen onLoggedIn={(t) => setToken(t)} />;
    }

    return <Inbox_ token={token} onLogout={handleLogout} />;
}
